import React from 'react';
import ReactECharts from 'echarts-for-react';
import { ViewMode, ChartOption } from './types';

interface ChartCardProps {
  title: string;
  option: ChartOption;
  height?: number;
  viewMode?: ViewMode;
  onToggleViewMode?: (m: ViewMode) => void;
}

const ChartCard: React.FC<ChartCardProps> = ({
  title,
  option,
  height = 350,
  viewMode,
  onToggleViewMode,
}) => {
  const isTimeBased = !!viewMode && !!onToggleViewMode;

  const handleExpand = () => {
    const dashboard = window.__dashboardModalHandler;
    if (dashboard) {
      dashboard({
        title,
        option,
        viewMode: isTimeBased ? viewMode : undefined,
        onToggleViewMode: isTimeBased ? onToggleViewMode : undefined,
      });
    }
  };

  return (
    <div style={styles.chartCardStyle}>
      {/* Centered chart title */}
      <div style={styles.chartTitleStyle}>{title}</div>
      {/* Header controls: time toggle + expand */}
      <div style={styles.chartControlsStyle}>
        {isTimeBased && (
          <>
            <button
              onClick={() => onToggleViewMode && onToggleViewMode('weekly')}
              style={
                viewMode === 'weekly'
                  ? styles.timeToggleActiveWeeklyStyle
                  : styles.timeToggleInactiveStyle
              }>
              Weekly
            </button>
            <button
              onClick={() => onToggleViewMode && onToggleViewMode('monthly')}
              style={
                viewMode === 'monthly'
                  ? styles.timeToggleActiveMonthlyStyle
                  : styles.timeToggleInactiveStyle
              }>
              Monthly
            </button>
          </>
        )}
        <button
          onClick={handleExpand}
          style={styles.expandButtonStyle}
          title="Expand">
          ⛶
        </button>
      </div>
      <ReactECharts
        option={option}
        style={{ height, position: 'relative', zIndex: 10 }}
      />
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  chartCardStyle: {
    backgroundColor: '#ffffff', // bg-white
    borderRadius: '16px', // rounded-2xl
    boxShadow:
      '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)', // shadow-md
    transition: 'box-shadow 300ms',
    padding: '24px', // p-6
    position: 'relative',
  } as React.CSSProperties,
  chartTitleStyle: {
    textAlign: 'center',
    fontSize: '15px',
    fontWeight: '600', // font-semibold
    color: '#4A4A4A',
    marginBottom: '12px', // mb-3
  } as React.CSSProperties,
  chartControlsStyle: {
    position: 'absolute',
    right: '16px', // right-4
    top: '16px', // top-4
    zIndex: 20,
    display: 'flex',
    alignItems: 'center',
    gap: '8px', // gap-2
  } as React.CSSProperties,
  timeToggleActiveWeeklyStyle: {
    padding: '2px 10px', // px-2.5 py-0.5
    borderRadius: '9999px', // rounded-full
    fontSize: '12px', // text-xs
    fontWeight: '500', // font-medium
    backgroundColor: '#F15A29',
    color: '#ffffff',
    border: 'none',
    cursor: 'pointer',
  },
  timeToggleActiveMonthlyStyle: {
    padding: '2px 10px', // px-2.5 py-0.5
    borderRadius: '9999px', // rounded-full
    fontSize: '12px', // text-xs
    fontWeight: '500', // font-medium
    backgroundColor: '#662D91',
    color: '#ffffff',
    border: 'none',
    cursor: 'pointer',
  },
  timeToggleInactiveStyle: {
    padding: '2px 10px', // px-2.5 py-0.5
    borderRadius: '9999px', // rounded-full
    fontSize: '12px', // text-xs
    fontWeight: '500', // font-medium
    backgroundColor: '#e5e7eb', // bg-gray-200
    color: '#4A4A4A',
    border: 'none',
    cursor: 'pointer',
  },
  expandButtonStyle: {
    padding: '2px 8px', // px-2 py-0.5
    borderRadius: '9999px', // rounded-full
    backgroundColor: '#f3f4f6', // bg-gray-100
    color: '#4A4A4A',
    fontSize: '12px', // text-xs
    border: 'none',
    cursor: 'pointer',
  },
};

export default ChartCard;
